import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useGlobalContext } from '../../../context/GlobalContext';

const CallToActionSection = () => {
  const { globalSettings: settings, resumeUrl } = useGlobalContext();
  const resources = [
    {
      title: "Detailed Resume",
      description: "Complete professional background and experience",
      icon: "FileText",
      action: "Download PDF",
      link: resumeUrl || "/assets/Resume/Resume PDF.pdf"
    },
    {
      title: "Portfolio Showcase",
      description: "View my latest projects and case studies",
      icon: "Briefcase",
      action: "View Projects",
      link: "/projects"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-success/5 opacity-50"></div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main CTA */}
        <div className="text-center mb-16 relative">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed mb-8">
            I'm always excited to discuss new projects and explore how we can create something amazing together. Let's have a conversation about your goals and challenges.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              title="Navigate to the Contact Page to Schedule a Call or Send an Inquiry"
              aria-label="Start a conversation with Sudhan"
            >
              <Button
                variant="primary"
                iconName="MessageCircle"
                iconPosition="right"
                className="px-8 py-4 text-lg"
              >
                Start a Conversation
              </Button>
            </Link>
            <Link 
              to="/projects"
              title="Navigate to Sudhan's Full Case Study Portfolio"
              aria-label="View technical case studies and projects"
            >
              <Button
                variant="outline"
                iconName="Eye"
                iconPosition="right"
                className="px-8 py-4 text-lg"
              >
                View My Work
              </Button>
            </Link>
          </div>
        </div>

        {/* Resources */}
        <div className="mb-16 relative">
          <h3 className="font-heading text-2xl font-semibold text-text-primary text-center mb-8">
            Downloadable Resources
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            {resources.map((resource, index) => {
              const isExternal = resource.link.endsWith('.pdf') || resource.link.startsWith('http');
              return (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:shadow-accent/5 transition-smooth group"
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-smooth">
                    <Icon
                      name={resource.icon}
                      size={24}
                      className="text-accent"
                    />
                  </div>

                  <h4 className="font-heading text-lg font-semibold text-text-primary mb-2">
                    {resource.title}
                  </h4>

                  <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                    {resource.description}
                  </p>

                  {isExternal ? (
                    <a
                      href={resource.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      title={`Download Resource: ${resource.title}`}
                      aria-label={`Download ${resource.title} document`}
                    >
                      <Button
                        variant="ghost"
                        iconName="Download"
                        iconPosition="right"
                        className="text-sm"
                      >
                        {resource.action}
                      </Button>
                    </a>
                  ) : (
                    <Link 
                      to={resource.link}
                      title={`Navigate to ${resource.title}`}
                      aria-label={`View ${resource.title}`}
                    >
                      <Button
                        variant="ghost"
                        iconName="ArrowRight"
                        iconPosition="right"
                        className="text-sm"
                      >
                        {resource.action}
                      </Button>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 lg:p-12 shadow-2xl relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-heading text-2xl font-semibold text-text-primary mb-4">
                Let's Collaborate
              </h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                Whether you're a student, researcher, or space enthusiast, feel free to reach out for collaboration, outreach, or project discussions. I actively respond within 24 hours and am always open to meaningful conversations in STEM, AI, and space research.
              </p>

              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-text-secondary">
                  <Icon name="Mail" size={20} className="text-accent" />
                  <span>{settings?.contact_email || 'sudhan.r.web@gmail.com'}</span>
                </div>
                <div className="flex items-center space-x-3 text-text-secondary">
                  <Icon name="Phone" size={20} className="text-accent" />
                  <span>{settings?.contact_phone || '+91 99409 80978'}</span>
                </div>
                <div className="flex items-center space-x-3 text-text-secondary">
                  <Icon name="Clock" size={20} className="text-accent" />
                  <span>Available Mon–Sat, 10AM–6PM IST</span>
                </div>
              </div>
            </div>
            <div className="text-center lg:text-right">
              <div className="inline-flex space-x-4">
                <a
                  href={settings?.social_linkedin || "https://www.linkedin.com/in/sudhan23082004/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center hover:bg-accent hover:text-white transition-smooth"
                  aria-label="Visit Sudhan's LinkedIn Profile"
                  title="Connect on LinkedIn"
                >
                  <Icon name="Linkedin" size={20} />
                </a>

                <a
                  href={settings?.social_github || "https://github.com/Sudhan-23082004"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center hover:bg-accent hover:text-white transition-smooth"
                  aria-label="View Sudhan's GitHub Repositories"
                  title="View GitHub Profile"
                >
                  <Icon name="Github" size={20} />
                </a>

                {settings?.contact_location && (
                  <a
                    href={settings.contact_location}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center hover:bg-accent hover:text-white transition-smooth"
                    aria-label="View Location on Map"
                    title="View Base Location"
                  >
                    <Icon name="MapPin" size={20} />
                  </a>
                )}

                <a
                  href={`mailto:${settings?.contact_email || 'sudhan.r.web@gmail.com'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center hover:bg-accent hover:text-white transition-smooth"
                  aria-label="Send an Email to Sudhan R"
                  title="Send Direct Email"
                >
                  <Icon name="Send" size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default CallToActionSection;
